import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@components/Table';
import 'moment/locale/pt-br';
import { Popconfirm, Button, message, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import api from '@services/api';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import { dateFormat } from '@utils/formatters';
import ModalTemplate from './ModalTemplate';

const SmsTemplates = () => {
  const { t } = useTranslation('sms-send');
  const { searchParams, setSearchParams } = useSearchParams({
    type: 'pagination',
  });

  const { page, linesPerPage } = useMemo(() => searchParams, [searchParams]);

  const { data, mutate } = useSwr(`/service-proxy/sms-templates`, {
    params: {
      page: page ? Number(page) : 1,
      linesPerPage: linesPerPage ?? 20,
    },
  });

  const handleDelete = useCallback(
    async (idToDelete: string) => {
      try {
        await api.delete(`/service-proxy/sms-templates/${idToDelete}`);
        message.success(t('template.deleted'));
      } catch (err) {
        message.error(t('template.error'));
      }
      mutate();
    },
    [mutate, t],
  );

  const messageColumn = useCallback((messageToFormat: string) => {
    return messageToFormat.length > 20 ? (
      <Tooltip title={messageToFormat}>
        {`${messageToFormat.substring(0, 20)}...`}
      </Tooltip>
    ) : (
      messageToFormat
    );
  }, []);

  const columns = useMemo(() => {
    const defaultColumns = [
      {
        title: t('table.name'),
        dataIndex: 'name',
      },
      {
        title: t('table.text'),
        dataIndex: 'text',
        render: (value: string) => messageColumn(value),
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.createdBy'),
        dataIndex: 'createdBy',
      },
      {
        title: t('columns.actions'),
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, record: any) => (
          <>
            <ModalTemplate defaultValues={record} mutate={mutate} />
            <Popconfirm
              title={t('pop-over.confirm-delete')}
              cancelText={t('pop-over.no')}
              okText={t('pop-over.yes')}
              onConfirm={() => handleDelete(record.id)}
            >
              <Button
                style={{
                  marginLeft: 8,
                  padding: '0 8px',
                }}
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </>
        ),
      },
    ];

    return defaultColumns;
  }, [t, mutate, handleDelete, messageColumn]);

  return (
    <>
      <Table
        title={t('title.template')}
        columns={columns}
        data={data}
        extraActions={
          <>
            <ModalTemplate mutate={mutate} />
          </>
        }
        onTableChange={setSearchParams}
        loading={!data}
        tableKey="sms-templates"
      />
    </>
  );
};

export default SmsTemplates;
